import React, { useEffect, useState, useMemo } from 'react';
import {
  Container, Typography, Paper, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Box, Chip, Alert, FormControlLabel, Switch
} from '@mui/material';
import AdminTable from '../../components/shared/AdminTable';
import billingApi from '../../services/billingApi';
import AdminPageSkeleton from '../../components/shared/AdminPageSkeleton';

const US_STATES = [
  { code: 'AL', name: 'Alabama' }, { code: 'AK', name: 'Alaska' }, { code: 'AZ', name: 'Arizona' },
  { code: 'AR', name: 'Arkansas' }, { code: 'CA', name: 'California' }, { code: 'CO', name: 'Colorado' },
  { code: 'CT', name: 'Connecticut' }, { code: 'DE', name: 'Delaware' }, { code: 'DC', name: 'District of Columbia' },
  { code: 'FL', name: 'Florida' }, { code: 'GA', name: 'Georgia' }, { code: 'HI', name: 'Hawaii' },
  { code: 'ID', name: 'Idaho' }, { code: 'IL', name: 'Illinois' }, { code: 'IN', name: 'Indiana' },
  { code: 'IA', name: 'Iowa' }, { code: 'KS', name: 'Kansas' }, { code: 'KY', name: 'Kentucky' },
  { code: 'LA', name: 'Louisiana' }, { code: 'ME', name: 'Maine' }, { code: 'MD', name: 'Maryland' },
  { code: 'MA', name: 'Massachusetts' }, { code: 'MI', name: 'Michigan' }, { code: 'MN', name: 'Minnesota' },
  { code: 'MS', name: 'Mississippi' }, { code: 'MO', name: 'Missouri' }, { code: 'MT', name: 'Montana' },
  { code: 'NE', name: 'Nebraska' }, { code: 'NV', name: 'Nevada' }, { code: 'NH', name: 'New Hampshire' },
  { code: 'NJ', name: 'New Jersey' }, { code: 'NM', name: 'New Mexico' }, { code: 'NY', name: 'New York' },
  { code: 'NC', name: 'North Carolina' }, { code: 'ND', name: 'North Dakota' }, { code: 'OH', name: 'Ohio' },
  { code: 'OK', name: 'Oklahoma' }, { code: 'OR', name: 'Oregon' }, { code: 'PA', name: 'Pennsylvania' },
  { code: 'RI', name: 'Rhode Island' }, { code: 'SC', name: 'South Carolina' }, { code: 'SD', name: 'South Dakota' },
  { code: 'TN', name: 'Tennessee' }, { code: 'TX', name: 'Texas' }, { code: 'UT', name: 'Utah' },
  { code: 'VT', name: 'Vermont' }, { code: 'VA', name: 'Virginia' }, { code: 'WA', name: 'Washington' },
  { code: 'WV', name: 'West Virginia' }, { code: 'WI', name: 'Wisconsin' }, { code: 'WY', name: 'Wyoming' },
];

const EMPTY_FORM = {
  state_code: '', state_name: '', rate_percent: '', effective_at: '', expires_at: '', description: ''
};

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function rateStatus(rate) {
  const now = new Date();
  if (!rate.is_active) return { label: 'Inactive', color: 'default' };
  if (new Date(rate.effective_at) > now) return { label: 'Scheduled', color: 'warning' };
  if (rate.expires_at && new Date(rate.expires_at) <= now) return { label: 'Expired', color: 'default' };
  return { label: 'Active', color: 'success' };
}

export default function TaxRateManagement() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [dialog, setDialog] = useState(false);
  const [editDialog, setEditDialog] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [editForm, setEditForm] = useState({});

  useEffect(() => { loadData(); }, []);

  async function loadData() {
    try {
      const data = await billingApi.get('/api/admin/tax/rates?limit=500');
      setRates(data.rates || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    try {
      setError('');
      if (!form.state_code || !form.rate_percent || !form.effective_at) {
        setError('State, rate, and effective date are required');
        return;
      }
      const state = US_STATES.find(s => s.code === form.state_code);
      await billingApi.post('/api/admin/tax/rates', {
        ...form,
        state_name: state?.name || form.state_code,
        rate_percent: parseFloat(form.rate_percent),
        expires_at: form.expires_at || null
      });
      setDialog(false);
      setForm({ ...EMPTY_FORM });
      setSuccess('Tax rate created');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  async function handleUpdate() {
    try {
      setError('');
      await billingApi.put(`/api/admin/tax/rates/${editDialog.id}`, {
        description: editForm.description,
        expires_at: editForm.expires_at || null,
        is_active: editForm.is_active
      });
      setEditDialog(null);
      setSuccess('Tax rate updated');
      loadData();
    } catch (err) {
      setError(err.message);
    }
  }

  function openEdit(rate) {
    setEditDialog(rate);
    setEditForm({
      description: rate.description || '',
      expires_at: rate.expires_at ? new Date(rate.expires_at).toISOString().slice(0, 16) : '',
      is_active: rate.is_active
    });
  }

  const columns = useMemo(() => [
    {
      field: 'state_code',
      headerName: 'State',
      width: 130,
      type: 'singleSelect',
      filterOptions: US_STATES.map(s => s.code),
      renderCell: (row) => (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>{row.state_code}</Typography>
          <Typography variant="caption" color="text.secondary">{row.state_name}</Typography>
        </Box>
      ),
    },
    {
      field: 'rate_percent',
      headerName: 'Rate %',
      width: 100,
      type: 'numeric',
      renderCell: (row) => `${parseFloat(row.rate_percent).toFixed(2)}%`,
    },
    {
      field: 'effective_at',
      headerName: 'Effective From',
      width: 170,
      type: 'date',
      renderCell: (row) => formatDate(row.effective_at),
    },
    {
      field: 'expires_at',
      headerName: 'Expires',
      width: 170,
      type: 'date',
      renderCell: (row) => row.expires_at ? formatDate(row.expires_at) : '—',
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 200,
      renderCell: (row) => (
        <Typography variant="body2" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {row.description || '—'}
        </Typography>
      ),
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      type: 'singleSelect',
      filterOptions: ['Active', 'Scheduled', 'Expired', 'Inactive'],
      valueGetter: ({ row }) => rateStatus(row).label,
      renderCell: (row) => {
        const status = rateStatus(row);
        return <Chip label={status.label} color={status.color} size="small" variant="outlined" />;
      },
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      sortable: false,
      filterable: false,
      renderCell: (row) => (
        <Button size="small" onClick={() => openEdit(row)}>Edit</Button>
      ),
    },
  ], []);

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <AdminPageSkeleton bare rows={5} />
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Tax Rates</Typography>
        <Button variant="contained" onClick={() => setDialog(true)}
          sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
          Add Rate
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <Paper variant="outlined" sx={{ height: 600 }}>
        <AdminTable
          rows={rates}
          columns={columns}
          defaultSort={[{ field: 'effective_at', direction: 'desc' }]}
          defaultPageSize={25}
        />
      </Paper>

      {/* Create Dialog */}
      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add Tax Rate</DialogTitle>
        <DialogContent>
          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>State</Typography>
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel>State</InputLabel>
            <Select value={form.state_code} label="State"
              onChange={e => {
                const st = US_STATES.find(s => s.code === e.target.value);
                setForm({ ...form, state_code: e.target.value, state_name: st?.name || '' });
              }}>
              {US_STATES.map(s => <MenuItem key={s.code} value={s.code}>{s.code} — {s.name}</MenuItem>)}
            </Select>
          </FormControl>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Rate</Typography>
          <TextField
            fullWidth type="number" label="Tax Rate (%)" value={form.rate_percent}
            onChange={e => setForm({ ...form, rate_percent: e.target.value })}
            helperText="e.g. 6.25 for 6.25%"
            inputProps={{ step: '0.01', min: '0', max: '20' }}
            sx={{ mb: 2 }}
          />

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Effective Date</Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <TextField
              fullWidth type="datetime-local" label="Effective From" value={form.effective_at}
              onChange={e => setForm({ ...form, effective_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              fullWidth type="datetime-local" label="Expires At (optional)" value={form.expires_at}
              onChange={e => setForm({ ...form, expires_at: e.target.value })}
              InputLabelProps={{ shrink: true }}
              helperText="Leave blank for indefinite"
            />
          </Box>

          <Typography variant="subtitle2" sx={{ mb: 1 }}>Description</Typography>
          <TextField
            fullWidth label="Description (optional)" value={form.description}
            onChange={e => setForm({ ...form, description: e.target.value })}
            helperText="Internal note about this rate"
          />

          <Alert severity="info" sx={{ mt: 2 }}>
            Creating a new rate for an existing state will automatically close (expire) the previous rate
            for that state at this rate's effective date.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreate}
            sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
            Create Rate
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editDialog} onClose={() => setEditDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Tax Rate</DialogTitle>
        <DialogContent>
          {editDialog && (
            <>
              <Box sx={{ bgcolor: 'grey.50', p: 2, borderRadius: 1, mb: 2, mt: 1 }}>
                <Typography variant="body2">
                  <strong>{editDialog.state_code}</strong> — {editDialog.state_name} @ {parseFloat(editDialog.rate_percent).toFixed(2)}%
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Effective from {formatDate(editDialog.effective_at)}
                </Typography>
              </Box>

              <Alert severity="info" sx={{ mb: 2 }}>
                Rate percent and effective date cannot be changed. To change the rate, create a new rate entry.
              </Alert>

              <TextField
                fullWidth label="Description" value={editForm.description}
                onChange={e => setEditForm({ ...editForm, description: e.target.value })}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth type="datetime-local" label="Expires At" value={editForm.expires_at}
                onChange={e => setEditForm({ ...editForm, expires_at: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="Leave blank for indefinite"
                sx={{ mb: 2 }}
              />
              <FormControlLabel
                control={<Switch checked={editForm.is_active} onChange={e => setEditForm({ ...editForm, is_active: e.target.checked })} />}
                label="Active"
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(null)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdate}
            sx={{ bgcolor: '#C8942A', '&:hover': { bgcolor: '#A6791F' } }}>
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
